import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Checkbox, Menu, MenuItem } from '@mui/material';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import getServices from '../../Services/get-service';
import putServices from '../../Services/put-service';
import swal from 'sweetalert';
import more from '../../Assets/images/more.svg';
import Loading from '../SelectValue/Loading';
import TopDCom from '../Savings.js/TopDCom';
import AddAssets from './AddAssests';

const statusOptions = ['', 'PENDING', 'APPROVED', 'REJECTED'];

const formatDate = (value) => {
  if (!value) return 'No date';
  return value.slice(0, 10);
};

const normalizeStatus = (status) => status || 'PENDING';

const formatStatusLabel = (status) => {
  const normalizedStatus = normalizeStatus(status);
  return normalizedStatus.charAt(0).toUpperCase() + normalizedStatus.slice(1).toLowerCase();
};

const getStatusClass = (status) => {
  switch (normalizeStatus(status).toUpperCase()) {
    case 'APPROVED':
      return 'bg-green-100 text-green-800';
    case 'REJECTED':
      return 'bg-red-100 text-red-800';
    case 'PENDING':
    default:
      return 'bg-yellow-100 text-yellow-800';
  }
};

const StatusBadge = ({ status }) => (
  <span className={`inline-flex rounded-[32px] px-3 py-2 text-sm font-medium ${getStatusClass(status)}`}>
    {formatStatusLabel(status)}
  </span>
);

export default function CustomAssetRequest({ titleFilter = '', readOnly = false }) {
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);
  const [recommendations, setRecommendations] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [recommendationStats, setRecommendationStats] = useState(null);
  const [search, setSearch] = useState(titleFilter);
  const [status, setStatus] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRecommendation, setSelectedRecommendation] = useState(null);
  const [approvalOpen, setApprovalOpen] = useState(false);
  const [recommendationToApprove, setRecommendationToApprove] = useState(null);

  const fetchRecommendations = useCallback(() => {
    setLoading(true);
    getServices.getAssetRecommendations({
      status,
      page,
      limit: rowsPerPage,
    }).then(
      (response) => {
        setLoading(false);
        if (response.data.status === '2000') {
          setRecommendations(response.data.data?.recommendations || []);
          setPagination(response.data.data?.pagination || null);
        } else {
          swal(response.data.description || 'Failed to fetch custom asset requests');
        }
      },
      (error) => {
        setLoading(false);
        swal(error?.response?.data?.description || 'Error fetching custom asset requests');
      }
    );
  }, [page, rowsPerPage, status]);

  const fetchRecommendationStats = useCallback(() => {
    getServices.getAssetRecommendationStats().then(
      (response) => {
        if (response.data.status === '2000') {
          setRecommendationStats(response.data.data || null);
        } else {
          swal(response.data.description || 'Failed to fetch custom asset request stats');
        }
      },
      (error) => {
        swal(error?.response?.data?.description || 'Error fetching custom asset request stats');
      }
    );
  }, []);

  useEffect(() => {
    fetchRecommendations();
  }, [fetchRecommendations]);

  useEffect(() => {
    fetchRecommendationStats();
  }, [fetchRecommendationStats]);

  useEffect(() => {
    setSearch(titleFilter);
  }, [titleFilter]);

  const visibleRecommendations = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return recommendations;

    return recommendations.filter((item) => {
      const searchText = [
        item.requestedItemName,
        item.description,
        item.userEmail,
        item.userFullName,
        item.status,
      ].filter(Boolean).join(' ').toLowerCase();

      return searchText.includes(query);
    });
  }, [recommendations, search]);

  const handleOpenMenu = (event, recommendation) => {
    setAnchorEl(event.currentTarget);
    setSelectedRecommendation(recommendation);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setSelectedRecommendation(null);
  };

  const approveRecommendation = (recommendation) => {
    handleCloseMenu();
    setRecommendationToApprove(recommendation);
    setApprovalOpen(true);
  };

  const closeApprovalDrawer = () => {
    setApprovalOpen(false);
    setRecommendationToApprove(null);
  };

  const rejectRecommendation = (recommendation) => {
    handleCloseMenu();

    swal({
      title: 'Reject custom asset request?',
      text: 'Enter a reason for rejecting this request.',
      content: {
        element: 'input',
        attributes: {
          placeholder: 'Reason',
          type: 'text',
        },
      },
      buttons: ['Cancel', 'Reject'],
      dangerMode: true,
    }).then((reason) => {
      if (!reason) return;

      setLoading(true);
      putServices.rejectAssetRecommendation(recommendation.id, { reason }).then(
        (response) => {
          setLoading(false);
          if (response.data.status === '2000') {
            swal(response.data.description || 'Product rejected and user notified successfully.');
            fetchRecommendations();
            fetchRecommendationStats();
          } else {
            swal(response.data.description || 'Failed to reject custom asset request');
          }
        },
        (error) => {
          setLoading(false);
          swal(error?.response?.data?.description || 'Error rejecting custom asset request');
        }
      );
    });
  };

  const handlePreviousPage = () => {
    if (pagination?.hasPrevious || page > 0) {
      setPage(page - 1);
    }
  };

  const handleNextPage = () => {
    if (pagination?.hasNext) {
      setPage(page + 1);
    }
  };

  const totalPages = pagination?.totalPages || 1;
  const currentPage = pagination?.currentPage ?? page;

  return (
    <>
      <Loading open={loading} />
      <AddAssets
        open2={approvalOpen}
        handleClose2={closeApprovalDrawer}
        recommendationToApprove={recommendationToApprove}
        onSaved={() => {
          closeApprovalDrawer();
          fetchRecommendations();
          fetchRecommendationStats();
        }}
      />

      <div className="my-8 flex flex-wrap items-center gap-4">
        <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="w-full sm:w-auto lg:col-span-2">
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search by item, name, or email address"
              className="border border-gray-300 focus:border-primary-color outline-none p-3 rounded w-full"
            />
          </div>
          <div className="w-full sm:w-auto">
            <select
              value={status}
              onChange={(event) => {
                setStatus(event.target.value);
                setPage(0);
              }}
              className="border w-full p-3 rounded bg-white"
            >
              {statusOptions.map((option) => (
                <option key={option} value={option}>
                  {option ? formatStatusLabel(option) : 'All requests'}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="grid sm:grid-cols-3 md:grid-cols-5 mb-10 gap-4">
        <TopDCom name="Total custom requests" count={recommendationStats?.totalRequests ?? 0} />
        <TopDCom name="Pending review" count={recommendationStats?.pendingReview ?? 0} />
        <TopDCom name="Approved and sourced" count={recommendationStats?.approvedAndSourced ?? 0} />
        <TopDCom name="Rejected requests" count={recommendationStats?.rejected ?? 0} />
      </div>

      <div className="mt-16">
        <div className="hidden md:flex flex-row justify-between items-center bg-primary-bg rounded-t-md rounded-r-md border-primary-border border p-4">
          <Checkbox />
          <p className="text-text flex-1 text-center whitespace-normal break-words">User</p>
          <p className="text-text flex-1 text-center whitespace-normal break-words">Requested item</p>
          <p className="text-text flex-1 text-center whitespace-normal break-words">Description</p>
          <p className="text-text flex-1 text-center whitespace-normal break-words">Date added</p>
          <p className="text-text flex-1 text-center whitespace-normal break-words">Status</p>
          {!readOnly && <p className="text-text flex-1 text-center whitespace-normal break-words">Action</p>}
        </div>

        {loading ? (
          <div>
            {[...Array(rowsPerPage)].map((_, index) => (
              <div
                key={index}
                className="flex flex-col md:flex-row justify-between items-start md:items-center bg-white border-b border-gray-200 p-4 gap-2"
              >
                <div className="w-6 md:w-[5%]">
                  <Skeleton width={24} height={24} />
                </div>
                {[...Array(readOnly ? 5 : 6)].map((__, skeletonIndex) => (
                  <div key={skeletonIndex} className="flex-1 text-left md:text-center">
                    <Skeleton width="70%" height={20} />
                  </div>
                ))}
              </div>
            ))}
          </div>
        ) : visibleRecommendations.length === 0 ? (
          <div className="text-center p-4 text-gray-500">
            No custom asset requests found.
          </div>
        ) : (
          <>
            <div className="md:hidden">
              {visibleRecommendations.map((row) => (
                <div
                  key={row.id}
                  className="border rounded-lg p-4 mb-3 bg-white shadow-sm"
                >
                  <p className="font-semibold capitalize">{row.requestedItemName || 'N/A'}</p>
                  <p className="text-sm text-gray-500 break-words">{row.userEmail || 'No email'}</p>
                  <p><b>User:</b> {row.userFullName || 'N/A'}</p>
                  <p><b>Description:</b> {row.description || 'N/A'}</p>
                  {row.linkToItem ? (
                    <p>
                      <b>Link:</b>{' '}
                      <a className="text-primary-color break-all" href={row.linkToItem} target="_blank" rel="noreferrer">
                        {row.linkToItem}
                      </a>
                    </p>
                  ) : null}
                  <p><b>Date added:</b> {formatDate(row.createdAt)}</p>
                  <p className="mt-2"><StatusBadge status={row.status} /></p>
                  {!readOnly && normalizeStatus(row.status) === 'PENDING' && (
                    <>
                      <div className="flex justify-end mt-2">
                        <img
                          src={more}
                          alt="More options"
                          className="cursor-pointer"
                          onClick={(event) => handleOpenMenu(event, row)}
                        />
                      </div>
                      <Menu
                        anchorEl={anchorEl}
                        open={Boolean(anchorEl) && selectedRecommendation?.id === row.id}
                        onClose={handleCloseMenu}
                        sx={{ '& .MuiPaper-root': { boxShadow: 'none' } }}
                      >
                        <MenuItem onClick={() => approveRecommendation(row)}>Approve Request</MenuItem>
                        <MenuItem onClick={() => rejectRecommendation(row)} sx={{ color: '#ef4444' }}>
                          Reject Request
                        </MenuItem>
                      </Menu>
                    </>
                  )}
                </div>
              ))}
            </div>

            <div className="hidden md:block">
              {visibleRecommendations.map((row) => (
                <div
                  key={row.id}
                  className="flex flex-row justify-between items-center bg-white border-b border-gray-200 p-4"
                >
                  <Checkbox />
                  <div className="flex-1 text-center break-words whitespace-normal min-w-0 px-1">
                    <p className="font-medium capitalize">{row.userFullName || 'N/A'}</p>
                    <p className="text-sm text-gray-500 break-words">{row.userEmail || 'No email'}</p>
                  </div>
                  <div className="flex-1 text-center break-words whitespace-normal min-w-0 px-1">
                    <p>{row.requestedItemName || 'N/A'}</p>
                    {row.linkToItem ? (
                      <a className="text-sm text-primary-color break-all" href={row.linkToItem} target="_blank" rel="noreferrer">
                        View link
                      </a>
                    ) : null}
                  </div>
                  <p className="flex-1 text-center break-words whitespace-normal min-w-0 px-1">{row.description || 'N/A'}</p>
                  <p className="flex-1 text-center break-words whitespace-normal min-w-0 px-1">{formatDate(row.createdAt)}</p>
                  <p className="flex-1 text-center break-words whitespace-normal min-w-0 px-1">
                    <StatusBadge status={row.status} />
                  </p>
                  {!readOnly && (
                    <div className="flex-1 text-center break-words whitespace-normal min-w-0 px-1">
                      {normalizeStatus(row.status) === 'PENDING' ? (
                        <>
                          <img
                            src={more}
                            alt="More options"
                            className="mx-auto cursor-pointer"
                            onClick={(event) => handleOpenMenu(event, row)}
                          />
                          <Menu
                            anchorEl={anchorEl}
                            open={Boolean(anchorEl) && selectedRecommendation?.id === row.id}
                            onClose={handleCloseMenu}
                            sx={{ '& .MuiPaper-root': { boxShadow: 'none' } }}
                          >
                            <MenuItem onClick={() => approveRecommendation(row)}>Approve Request</MenuItem>
                            <MenuItem onClick={() => rejectRecommendation(row)} sx={{ color: '#ef4444' }}>
                              Reject Request
                            </MenuItem>
                          </Menu>
                        </>
                      ) : (
                        <span className="text-sm text-gray-400"></span>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </>
        )}

        <div className="flex flex-col sm:flex-row justify-between items-center gap-3 mt-8 mb-10">
          <button
            onClick={handlePreviousPage}
            disabled={currentPage === 0}
            className={`px-4 py-2 rounded-lg border ${
              currentPage === 0
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-white text-text hover:border-primary-color'
            }`}
          >
            Previous
          </button>
          <p className="text-text text-sm sm:text-base">
            Page {currentPage + 1} of {totalPages}
          </p>
          <button
            onClick={handleNextPage}
            disabled={!pagination?.hasNext}
            className={`px-4 py-2 rounded-lg border ${
              !pagination?.hasNext
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-white text-text hover:border-primary-color'
            }`}
          >
            Next
          </button>
        </div>
      </div>
    </>
  );
}
